(() => {
  const ROOT_SELECTOR = '[data-step-timeline-init]';
  const MOBILE_QUERY = '(max-width: 749px)';

  const DEFAULT_ACTIVATION_RATIO = 0.5;
  const MOBILE_ACTIVATION_RATIO = 0.58;

  const DEPENDENCY_RETRY_LIMIT = 40;
  const DEPENDENCY_RETRY_DELAY = 100;
  const LATE_REFRESH_DELAYS = [200, 700, 1400];

  const mobileQuery = window.matchMedia(MOBILE_QUERY);

  let dependencyRetryTimer = null;
  let dependencyRetryAttempts = 0;
  let breakpointFrameOne = null;
  let breakpointFrameTwo = null;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function hasGsap() {
    return typeof window.gsap !== 'undefined';
  }


  /* =========================================
     ROOT HELPERS
     ========================================= */

  function getRoots(root = document) {
    const roots = [];

    if (
      root.matches &&
      root.matches(ROOT_SELECTOR)
    ) {
      roots.push(root);
    }

    if (root.querySelectorAll) {
      roots.push(
        ...root.querySelectorAll(ROOT_SELECTOR)
      );
    }

    return roots;
  }


  /* =========================================
     TIMELINE INSTANCE
     ========================================= */

  function initTimeline(root) {
    if (
      !root ||
      typeof root._wamboltTimelineCleanup ===
        'function'
    ) {
      return;
    }

    const wrapper = root.querySelector(
      '.step-timeline__wrapper'
    );

    const line = root.querySelector(
      '[data-step-timeline-line]'
    );

    const fill = root.querySelector(
      '[data-step-timeline-fill]'
    );

    const items = Array.from(
      root.querySelectorAll(
        '[data-step-timeline-item]'
      )
    );

    if (
      !wrapper ||
      !line ||
      !fill ||
      !items.length
    ) {
      return;
    }

    const markers = items.map(
      (item) =>
        item.querySelector(
          '[data-step-timeline-marker]'
        )
    );

    if (markers.some((marker) => !marker)) {
      return;
    }

    const images = Array.from(
      root.querySelectorAll('img')
    );

    let currentIndex = -2;
    let rafId = null;
    let initialFrameOne = null;
    let initialFrameTwo = null;
    let resizeObserver = null;
    let destroyed = false;

    const lateRefreshTimers = [];
    const pendingImages = new Set();


    /* =========================================
       RESPONSIVE ACTIVATION POINT
       ========================================= */

    function getDesktopActivationRatio() {
      const configuredRatio = Number.parseFloat(
        root.dataset.stepTimelineActivation
      );

      if (!Number.isFinite(configuredRatio)) {
        return DEFAULT_ACTIVATION_RATIO;
      }

      return gsap.utils.clamp(
        0,
        1,
        configuredRatio
      );
    }

    function getActivationRatio() {
      return mobileQuery.matches
        ? MOBILE_ACTIVATION_RATIO
        : getDesktopActivationRatio();
    }

    function getActivationY() {
      /*
       * Marker rectangles and the activation point are
       * both viewport-relative. This keeps the component
       * independent from whether window or .page-wrapper
       * is the active scroll source.
       */
      return (
        window.innerHeight *
        getActivationRatio()
      );
    }


    /* =========================================
       STATIC TIMELINE GEOMETRY
       ========================================= */

    function measureTimeline() {
      if (
        destroyed ||
        !root.isConnected
      ) {
        return;
      }

      const wrapperRect =
        wrapper.getBoundingClientRect();

      const markerPositions =
        markers.map((marker) => {
          const rect =
            marker.getBoundingClientRect();

          return (
            rect.top -
            wrapperRect.top +
            rect.height / 2
          );
        });

      const firstMarkerPosition =
        markerPositions[0] || 0;

      const lastMarkerPosition =
        markerPositions[
          markerPositions.length - 1
        ] || firstMarkerPosition;

      const timelineSpan = Math.max(
        lastMarkerPosition -
          firstMarkerPosition,
        0
      );

      line.style.top =
        `${firstMarkerPosition}px`;

      line.style.height =
        `${timelineSpan}px`;
    }


    /* =========================================
       ITEM STATES
       ========================================= */

    function setCurrentIndex(index) {
      if (
        destroyed ||
        index === currentIndex
      ) {
        return;
      }

      currentIndex = index;

      items.forEach(
        (item, itemIndex) => {
          const status =
            index >= 0 &&
            itemIndex <= index
              ? 'active'
              : 'inactive';

          item.setAttribute(
            'data-status',
            status
          );

          item.toggleAttribute(
            'data-current',
            itemIndex === index
          );

          item.toggleAttribute(
            'data-previous',
            itemIndex === index - 1
          );

          item.toggleAttribute(
            'data-next',
            itemIndex === index + 1
          );
        }
      );
    }


    /* =========================================
       LIVE SCROLL CALCULATION
       ========================================= */

    function updateTimeline() {
      rafId = null;

      if (
        destroyed ||
        !root.isConnected
      ) {
        return;
      }

      const activationY = getActivationY();

      const markerCenters = markers.map(
        (marker) => {
          const rect =
            marker.getBoundingClientRect();

          return (
            rect.top +
            rect.height / 2
          );
        }
      );

      if (!markerCenters.length) {
        return;
      }

      const firstY = markerCenters[0];
      const lastY =
        markerCenters[
          markerCenters.length - 1
        ];

      let progress = 0;

      if (activationY <= firstY) {
        progress = 0;
      } else if (activationY >= lastY) {
        progress = 1;
      } else {
        const visibleSpan = lastY - firstY;

        if (visibleSpan > 0) {
          progress =
            (activationY - firstY) /
            visibleSpan;
        }
      }

      progress = gsap.utils.clamp(
        0,
        1,
        progress
      );

      gsap.set(fill, {
        scaleY: progress,
        transformOrigin: 'top center',
      });

      let activeIndex = -1;

      markerCenters.forEach(
        (center, index) => {
          if (center <= activationY) {
            activeIndex = index;
          }
        }
      );

      setCurrentIndex(activeIndex);
    }


    /* =========================================
       RAF THROTTLE
       ========================================= */

    function requestUpdate() {
      if (
        destroyed ||
        rafId !== null
      ) {
        return;
      }

      rafId = window.requestAnimationFrame(
        updateTimeline
      );
    }


    /* =========================================
       REFRESH
       ========================================= */

    function refreshTimeline() {
      if (destroyed) {
        return;
      }

      measureTimeline();
      requestUpdate();
    }


    /* =========================================
       INITIAL STATE
       ========================================= */

    setCurrentIndex(-1);

    gsap.set(fill, {
      scaleY: 0,
      transformOrigin: 'top center',
    });


    /* =========================================
       SCROLL LISTENERS
       ========================================= */

    /*
     * Element scroll events do not bubble, but capture
     * mode allows document to observe scrolling descendants.
     * Keep the window listener as well for window scrolling.
     */
    document.addEventListener(
      'scroll',
      requestUpdate,
      {
        passive: true,
        capture: true,
      }
    );

    window.addEventListener(
      'scroll',
      requestUpdate,
      {
        passive: true,
      }
    );


    /* =========================================
       RESIZE
       ========================================= */

    window.addEventListener(
      'resize',
      refreshTimeline,
      {
        passive: true,
      }
    );

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        refreshTimeline,
        {
          passive: true,
        }
      );

      window.visualViewport.addEventListener(
        'scroll',
        requestUpdate,
        {
          passive: true,
        }
      );
    }

    if (
      typeof ResizeObserver !== 'undefined'
    ) {
      resizeObserver =
        new ResizeObserver(
          refreshTimeline
        );

      resizeObserver.observe(wrapper);
    }


    /* =========================================
       MEDIA AND FONT LOADS
       ========================================= */

    function handleImageLoad(event) {
      pendingImages.delete(event.currentTarget);
      refreshTimeline();
    }

    images.forEach((image) => {
      if (image.complete) {
        return;
      }

      pendingImages.add(image);

      image.addEventListener(
        'load',
        handleImageLoad,
        {
          once: true,
        }
      );
    });

    if (
      document.fonts &&
      document.fonts.ready
    ) {
      document.fonts.ready.then(() => {
        if (
          !destroyed &&
          root.isConnected
        ) {
          refreshTimeline();
        }
      });
    }


    /* =========================================
       INITIAL BUILD
       ========================================= */

    initialFrameOne =
      window.requestAnimationFrame(() => {
        initialFrameOne = null;

        initialFrameTwo =
          window.requestAnimationFrame(() => {
            initialFrameTwo = null;

            if (destroyed) {
              return;
            }

            refreshTimeline();

            root.setAttribute(
              'data-step-timeline-ready',
              ''
            );

            root.setAttribute(
              'data-step-timeline-mode',
              mobileQuery.matches
                ? 'mobile'
                : 'desktop'
            );
          });
      });

    LATE_REFRESH_DELAYS.forEach((delay) => {
      lateRefreshTimers.push(
        window.setTimeout(
          refreshTimeline,
          delay
        )
      );
    });


    /* =========================================
       PUBLIC INSTANCE HOOKS
       ========================================= */

    root._wamboltTimelineRefresh =
      refreshTimeline;

    root._wamboltTimelineCleanup =
      function cleanupTimeline() {
        if (destroyed) {
          return;
        }

        destroyed = true;

        document.removeEventListener(
          'scroll',
          requestUpdate,
          true
        );

        window.removeEventListener(
          'scroll',
          requestUpdate
        );

        window.removeEventListener(
          'resize',
          refreshTimeline
        );

        if (window.visualViewport) {
          window.visualViewport.removeEventListener(
            'resize',
            refreshTimeline
          );

          window.visualViewport.removeEventListener(
            'scroll',
            requestUpdate
          );
        }

        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }

        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
          rafId = null;
        }

        if (initialFrameOne !== null) {
          window.cancelAnimationFrame(
            initialFrameOne
          );

          initialFrameOne = null;
        }

        if (initialFrameTwo !== null) {
          window.cancelAnimationFrame(
            initialFrameTwo
          );

          initialFrameTwo = null;
        }

        lateRefreshTimers.forEach(
          (timer) => {
            window.clearTimeout(timer);
          }
        );

        pendingImages.forEach((image) => {
          image.removeEventListener(
            'load',
            handleImageLoad
          );
        });

        pendingImages.clear();

        root.removeAttribute(
          'data-step-timeline-ready'
        );

        root.removeAttribute(
          'data-step-timeline-mode'
        );

        delete root._wamboltTimelineRefresh;
        delete root._wamboltTimelineCleanup;
      };
  }


  /* =========================================
     COMPONENT LIFECYCLE
     ========================================= */

  function initAll(root = document) {
    if (!hasGsap()) {
      return false;
    }

    getRoots(root).forEach(initTimeline);

    return true;
  }

  function destroyAll(root = document) {
    getRoots(root).forEach((timelineRoot) => {
      if (
        typeof timelineRoot
          ._wamboltTimelineCleanup ===
          'function'
      ) {
        timelineRoot._wamboltTimelineCleanup();
      }
    });
  }

  function refreshAll(root = document) {
    getRoots(root).forEach((timelineRoot) => {
      if (
        typeof timelineRoot
          ._wamboltTimelineRefresh ===
          'function'
      ) {
        timelineRoot._wamboltTimelineRefresh();
      }
    });
  }


  /* =========================================
     DEPENDENCY-SAFE START
     ========================================= */

  function clearDependencyRetry() {
    if (dependencyRetryTimer !== null) {
      window.clearTimeout(
        dependencyRetryTimer
      );

      dependencyRetryTimer = null;
    }
  }

  function start() {
    if (initAll(document)) {
      dependencyRetryAttempts = 0;
      clearDependencyRetry();
      return;
    }

    if (
      dependencyRetryAttempts >=
      DEPENDENCY_RETRY_LIMIT
    ) {
      clearDependencyRetry();

      console.warn(
        'Wambolt Why Timeline could not initialize because GSAP is unavailable.'
      );

      return;
    }

    dependencyRetryAttempts += 1;
    clearDependencyRetry();

    dependencyRetryTimer =
      window.setTimeout(
        start,
        DEPENDENCY_RETRY_DELAY
      );
  }


  /* =========================================
     RESPONSIVE REFRESH
     ========================================= */

  function scheduleResponsiveRefresh() {
    if (breakpointFrameOne !== null) {
      window.cancelAnimationFrame(
        breakpointFrameOne
      );
    }

    if (breakpointFrameTwo !== null) {
      window.cancelAnimationFrame(
        breakpointFrameTwo
      );
    }

    breakpointFrameOne =
      window.requestAnimationFrame(() => {
        breakpointFrameOne = null;

        breakpointFrameTwo =
          window.requestAnimationFrame(() => {
            breakpointFrameTwo = null;

            getRoots(document).forEach(
              (timelineRoot) => {
                timelineRoot.setAttribute(
                  'data-step-timeline-mode',
                  mobileQuery.matches
                    ? 'mobile'
                    : 'desktop'
                );
              }
            );

            refreshAll(document);
          });
      });
  }

  if (
    typeof mobileQuery.addEventListener ===
    'function'
  ) {
    mobileQuery.addEventListener(
      'change',
      scheduleResponsiveRefresh
    );
  } else if (
    typeof mobileQuery.addListener ===
    'function'
  ) {
    mobileQuery.addListener(
      scheduleResponsiveRefresh
    );
  }


  /* =========================================
     INITIAL LOAD
     ========================================= */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }


  /* =========================================
     LENIS CHANGES
     ========================================= */

  window.addEventListener(
    'wambolt:lenis:ready',
    scheduleResponsiveRefresh
  );


  /* =========================================
     SHOPIFY / BARBA LIFECYCLE
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (!hasGsap()) {
        start();
        return;
      }

      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:reorder',
    scheduleResponsiveRefresh
  );
})();